import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday as dfIsToday,
  addMonths,
  format,
  parseISO,
} from 'date-fns';
import { de } from 'date-fns/locale';
import styles from './FloatingCalendar.module.css';

const ICON_SIZE = 48;
const PANEL_WIDTH = 340;
const EDGE_MARGIN = 16;
const CLICK_THRESHOLD = 5; // px – darunter gilt als Klick, nicht Drag
const STORAGE_KEY = 'floatingCalendar.position';

/** Normalisiert ein dueDate (ISO oder YYYY-MM-DD) zu einem Date oder null. */
function parseDueDate(raw) {
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

/** Mappt eine Prioritaet auf eine Punkt-/Akzentfarbe. */
function priorityColor(priority) {
  const p = (priority || '').toString().toLowerCase();
  if (p === 'high' || p === 'urgent') return '#dc3545';
  if (p === 'medium') return '#ffc107';
  if (p === 'low') return '#28a745';
  return '#4a9eff';
}

/** Snap einen Punkt an den naechstgelegenen Bildschirmrand. */
function snapToEdge(left, top) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const maxX = vw - ICON_SIZE - EDGE_MARGIN;
  const maxY = vh - ICON_SIZE - EDGE_MARGIN;

  const clampedLeft = Math.max(EDGE_MARGIN, Math.min(maxX, left));
  const clampedTop = Math.max(EDGE_MARGIN, Math.min(maxY, top));

  const distLeft = clampedLeft - EDGE_MARGIN;
  const distRight = maxX - clampedLeft;
  const distTop = clampedTop - EDGE_MARGIN;
  const distBottom = maxY - clampedTop;
  const min = Math.min(distLeft, distRight, distTop, distBottom);

  if (min === distLeft)  return { left: EDGE_MARGIN, top: clampedTop };
  if (min === distRight) return { left: maxX, top: clampedTop };
  if (min === distTop)   return { left: clampedLeft, top: EDGE_MARGIN };
  return { left: clampedLeft, top: maxY };
}

function loadInitialPosition() {
  if (typeof window === 'undefined') {
    return { left: EDGE_MARGIN, top: EDGE_MARGIN };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed.left === 'number' && typeof parsed.top === 'number') {
        const snapped = snapToEdge(parsed.left, parsed.top);
        return snapped;
      }
    }
  } catch (_) { /* ignore */ }
  // Default: unten rechts
  return snapToEdge(
    window.innerWidth - ICON_SIZE - EDGE_MARGIN,
    window.innerHeight - ICON_SIZE - EDGE_MARGIN
  );
}

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/**
 * Floating, frei verschiebbare Kalender-Widget.
 * Zeigt Tickets mit dueDate im Monatsgrid an (read-only).
 *
 * @param {Object} props
 * @param {Object<string, any>} [props.tasks] - Map von taskId -> Ticket (wie data.tasks im TicketBoard).
 * @param {(ticket: object) => void} [props.onTicketClick] - Optional: Klick auf ein Ticket.
 */
export default function FloatingCalendar({ tasks = {}, onTicketClick }) {
  const [pos, setPos] = useState(loadInitialPosition);
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date());

  const dragState = useRef({
    dragging: false,
    moved: false,
    startPointerX: 0,
    startPointerY: 0,
    startLeft: 0,
    startTop: 0,
    pointerId: null,
  });

  // === Tickets mit dueDate, nach Tag gruppiert ===
  const ticketsByDay = useMemo(() => {
    const map = {};
    const all = tasks && typeof tasks === 'object' ? Object.values(tasks) : [];
    all.forEach((t) => {
      const d = parseDueDate(t && t.dueDate);
      if (!d) return;
      const key = format(d, 'yyyy-MM-dd');
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    // Jeden Tag nach Prioritaet sortieren (high -> low)
    const rank = { urgent: 0, high: 1, medium: 2, low: 3 };
    Object.values(map).forEach((arr) => {
      arr.sort((a, b) => {
        const ra = rank[(a.priority || '').toLowerCase()] ?? 9;
        const rb = rank[(b.priority || '').toLowerCase()] ?? 9;
        return ra - rb;
      });
    });
    return map;
  }, [tasks]);

  // Gesamtzahl Tickets mit dueDate (fuer das Badge)
  const totalTickets = useMemo(
    () => Object.values(ticketsByDay).reduce((s, a) => s + a.length, 0),
    [ticketsByDay]
  );

  // === Kalendergrid ===
  const days = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [currentMonth]);

  const selectedKey = format(selectedDate, 'yyyy-MM-dd');
  const selectedTickets = ticketsByDay[selectedKey] || [];

  // === Pointer-Drag mit Snap-to-Edge ===
  const handlePointerDown = useCallback((e) => {
    if (e.button !== undefined && e.button !== 0) return; // nur linke Maustaste
    dragState.current = {
      dragging: true,
      moved: false,
      startPointerX: e.clientX,
      startPointerY: e.clientY,
      startLeft: pos.left,
      startTop: pos.top,
      pointerId: e.pointerId,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    setIsDragging(true);
  }, [pos.left, pos.top]);

  const handlePointerMove = useCallback((e) => {
    const ds = dragState.current;
    if (!ds.dragging) return;
    const dx = e.clientX - ds.startPointerX;
    const dy = e.clientY - ds.startPointerY;
    if (Math.abs(dx) > CLICK_THRESHOLD || Math.abs(dy) > CLICK_THRESHOLD) {
      ds.moved = true;
    }
    const maxX = window.innerWidth - ICON_SIZE - EDGE_MARGIN;
    const maxY = window.innerHeight - ICON_SIZE - EDGE_MARGIN;
    let newLeft = ds.startLeft + dx;
    let newTop = ds.startTop + dy;
    newLeft = Math.max(EDGE_MARGIN, Math.min(maxX, newLeft));
    newTop = Math.max(EDGE_MARGIN, Math.min(maxY, newTop));
    setPos({ left: newLeft, top: newTop });
  }, []);

  const handlePointerUp = useCallback((e) => {
    const ds = dragState.current;
    if (!ds.dragging) return;
    try { e.currentTarget.releasePointerCapture(ds.pointerId); } catch (_) { /* ignore */ }
    ds.dragging = false;
    setIsDragging(false);
    if (ds.moved) {
      // Snap an den nahsten Rand + persistieren
      setPos((p) => {
        const snapped = snapToEdge(p.left, p.top);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(snapped)); } catch (_) { /* ignore */ }
        return snapped;
      });
    } else {
      // reiner Klick -> Panel togglen
      setIsOpen((o) => !o);
    }
  }, []);

  // Position beim Resize-Fenster re-snapppen
  useEffect(() => {
    const onResize = () => {
      setPos((p) => snapToEdge(p.left, p.top));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ESC schliesst das Panel
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // === Panel-Position (smart neben das Icon, geclampt ins Viewport) ===
  const panelPosition = useMemo(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const iconOnRight = pos.left > vw / 2;
    const estPanelHeight = 460;
    let left;
    if (iconOnRight) {
      left = pos.left - PANEL_WIDTH - 10;
    } else {
      left = pos.left + ICON_SIZE + 10;
    }
    left = Math.max(EDGE_MARGIN, Math.min(vw - PANEL_WIDTH - EDGE_MARGIN, left));
    const top = Math.max(EDGE_MARGIN, Math.min(vh - estPanelHeight - EDGE_MARGIN, pos.top));
    return { left, top };
  }, [pos.left, pos.top]);

  const monthLabel = format(currentMonth, 'MMMM yyyy', { locale: de });

  return (
    <>
      <button
        type="button"
        className={`${styles.iconButton} ${isDragging ? styles.dragging : ''}`}
        style={{ left: pos.left, top: pos.top }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        aria-label={`Kalender öffnen. ${totalTickets} Ticket(s) mit Fälligkeit.`}
        title="Kalender (ziehbar, klicken zum Öffnen)"
      >
        <i className={`bi bi-calendar3 ${styles.icon}`} />
        {totalTickets > 0 && (
          <span className={styles.iconBadge}>
            {totalTickets > 99 ? '99+' : totalTickets}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className={styles.panel}
          style={{ left: panelPosition.left, top: panelPosition.top, width: PANEL_WIDTH }}
          role="dialog"
          aria-label={`Kalender ${monthLabel}`}
        >
          <div className={styles.panelHeader}>
            <div className={styles.monthNav}>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => setCurrentMonth((m) => addMonths(m, -1))}
                aria-label="Vorheriger Monat"
              >
                <i className="bi bi-chevron-left" />
              </button>
              <span className={styles.monthTitle}>{monthLabel}</span>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
                aria-label="Nächster Monat"
              >
                <i className="bi bi-chevron-right" />
              </button>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => setIsOpen(false)}
              aria-label="Kalender schliessen"
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          <div className={styles.weekdayRow}>
            {WEEKDAYS.map((w) => (
              <div key={w} className={styles.weekday}>{w}</div>
            ))}
          </div>

          <div className={styles.dayGrid}>
            {days.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const dayTickets = ticketsByDay[key] || [];
              const outside = !isSameMonth(day, currentMonth);
              const isToday = dfIsToday(day);
              const isSelected = isSameDay(day, selectedDate);
              const dotCount = Math.min(dayTickets.length, 3);

              const classes = [styles.day];
              if (outside) classes.push(styles.dayOutside);
              if (isToday) classes.push(styles.dayToday);
              if (isSelected) classes.push(styles.daySelected);

              return (
                <button
                  type="button"
                  key={key}
                  className={classes.join(' ')}
                  onClick={() => setSelectedDate(day)}
                  aria-label={`${format(day, 'dd.MM.yyyy')}${dayTickets.length ? `, ${dayTickets.length} Ticket(s)` : ''}`}
                >
                  <span className={styles.dayNumber}>{format(day, 'd')}</span>
                  {dotCount > 0 && (
                    <span className={styles.dayDots}>
                      {Array.from({ length: dotCount }).map((_, i) => (
                        <span
                          key={i}
                          className={styles.dayDot}
                          style={!isSelected ? { backgroundColor: priorityColor(dayTickets[i].priority) } : undefined}
                        />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className={styles.listHeader}>
            {format(selectedDate, 'EEEE, dd.MM.yyyy', { locale: de })}
            {selectedTickets.length > 0 && ` · ${selectedTickets.length}`}
          </div>

          <div className={styles.ticketList}>
            {selectedTickets.length === 0 ? (
              <div className={styles.emptyState}>Keine Tickets mit Fälligkeit an diesem Tag.</div>
            ) : (
              selectedTickets.map((t) => {
                const done = t.status === 'done';
                const bucketTitle = t.bucket && (t.bucket.title || (typeof t.bucket === 'object' ? '' : ''));
                return (
                  <div
                    key={t._id || t.id}
                    className={`${styles.ticketItem} ${done ? styles.ticketDone : ''}`}
                    onClick={() => onTicketClick && onTicketClick(t)}
                    role={onTicketClick ? 'button' : undefined}
                    tabIndex={onTicketClick ? 0 : undefined}
                  >
                    <span
                      className={styles.ticketPriority}
                      style={{ backgroundColor: priorityColor(t.priority) }}
                    />
                    <div className={styles.ticketInfo}>
                      <div className={styles.ticketTitle}>{t.title}</div>
                      <div className={styles.ticketMeta}>
                        {bucketTitle ? `${bucketTitle}` : ''}
                        {bucketTitle && t.project ? ' · ' : ''}
                        {t.project ? t.project : ''}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </>
  );
}
